import { api } from './api';

export interface CashRegisterDto {
  id: number;
  registerCode: string;
  registerName: string;
  branchId?: number;
  assignedUserId?: number;
  openingBalance: number;
  currentBalance: number;
  currency: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface CashTransactionDto {
  id: number;
  cashRegisterId: number;
  transactionDate: string;
  transactionType: number; // 1: Income, 2: Expense
  category: number; // 1: Sales, 2: Purchase, 3: Service, 4: Expense, 5: Transfer, 6: Adjustment, 7: OpeningBalance
  amount: number;
  balanceAfter: number;
  description?: string;
  reference?: string;
  relatedInvoiceId?: number;
  relatedDocumentId?: number;
  invoicePaymentId?: number;
  expenseId?: number;
  serviceId?: number;
  createdBy?: number;
  createdAt: string;
}

export interface ExpenseDto {
  id: number;
  expenseNo: string;
  expenseDate: string;
  expenseTypeId: number;
  cashRegisterId: number;
  amount: number;
  description?: string;
  receiptNo?: string;
  branchId?: number;
  createdAt: string;
}

export interface ExpenseTypeDto {
  id: number;
  typeCode: string;
  typeName: string;
  description?: string;
  isActive: boolean;
}

export interface ServiceDto {
  id: number;
  serviceNo: string;
  serviceDate: string;
  serviceTypeId: number;
  accountId?: number;
  cashRegisterId: number;
  amount: number;
  description?: string;
  branchId?: number;
  createdAt: string;
}

export interface ServiceTypeDto {
  id: number;
  typeCode: string;
  typeName: string;
  description?: string;
  isActive: boolean;
}

// Cash Register Services
export const fetchCashRegisters = async (): Promise<CashRegisterDto[]> => {
  const { data } = await api.get<CashRegisterDto[]>('/cashregisters');
  return data;
};

export const createCashRegister = async (register: Partial<CashRegisterDto>): Promise<CashRegisterDto> => {
  const { data } = await api.post<CashRegisterDto>('/cashregisters', register);
  return data;
};

export const updateCashRegister = async (id: number, register: Partial<CashRegisterDto>): Promise<CashRegisterDto> => {
  const { data } = await api.put<CashRegisterDto>(`/cashregisters/${id}`, register);
  return data;
};

export const deleteCashRegister = async (id: number): Promise<void> => {
  await api.delete(`/cashregisters/${id}`);
};

export const getCashRegisterBalance = async (id: number): Promise<{ openingBalance: number; currentBalance: number; totalIncome: number; totalExpense: number; currency: string }> => {
  const { data } = await api.get(`/cashregisters/${id}/balance`);
  return data;
};

// Cash Transaction Services
export const fetchCashTransactions = async (cashRegisterId?: number, startDate?: string, endDate?: string): Promise<CashTransactionDto[]> => {
  const params: any = {};
  if (cashRegisterId) params.cashRegisterId = cashRegisterId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  
  const { data } = await api.get<CashTransactionDto[]>('/cashtransactions', { params });
  return data;
};

export const createCashTransaction = async (transaction: Partial<CashTransactionDto>): Promise<CashTransactionDto> => {
  const { data } = await api.post<CashTransactionDto>('/cashtransactions', transaction);
  return data;
};

// Expense Services
export const fetchExpenses = async (): Promise<ExpenseDto[]> => {
  const { data } = await api.get<ExpenseDto[]>('/expenses');
  return data;
};

export const createExpense = async (expense: Partial<ExpenseDto>): Promise<ExpenseDto> => {
  const { data } = await api.post<ExpenseDto>('/expenses', expense);
  return data;
};

export const updateExpense = async (id: number, expense: Partial<ExpenseDto>): Promise<ExpenseDto> => {
  const { data } = await api.put<ExpenseDto>(`/expenses/${id}`, expense);
  return data;
};

export const deleteExpense = async (id: number): Promise<void> => {
  await api.delete(`/expenses/${id}`);
};

// Expense Type Services
export const fetchExpenseTypes = async (): Promise<ExpenseTypeDto[]> => {
  const { data } = await api.get<ExpenseTypeDto[]>('/expensetypes');
  return data;
};

export const createExpenseType = async (expenseType: Partial<ExpenseTypeDto>): Promise<ExpenseTypeDto> => {
  const { data } = await api.post<ExpenseTypeDto>('/expensetypes', expenseType);
  return data;
};

export const updateExpenseType = async (id: number, expenseType: Partial<ExpenseTypeDto>): Promise<ExpenseTypeDto> => {
  const { data } = await api.put<ExpenseTypeDto>(`/expensetypes/${id}`, expenseType);
  return data;
};

export const deleteExpenseType = async (id: number): Promise<void> => {
  await api.delete(`/expensetypes/${id}`);
};

// Service Services
export const fetchServices = async (): Promise<ServiceDto[]> => {
  const { data } = await api.get<ServiceDto[]>('/services');
  return data;
};

export const createService = async (service: Partial<ServiceDto>): Promise<ServiceDto> => {
  const { data } = await api.post<ServiceDto>('/services', service);
  return data;
};

export const updateService = async (id: number, service: Partial<ServiceDto>): Promise<ServiceDto> => {
  const { data } = await api.put<ServiceDto>(`/services/${id}`, service);
  return data;
};

export const deleteService = async (id: number): Promise<void> => {
  await api.delete(`/services/${id}`);
};

// Service Type Services
export const fetchServiceTypes = async (): Promise<ServiceTypeDto[]> => {
  const { data } = await api.get<ServiceTypeDto[]>('/servicetypes');
  return data;
};

export const createServiceType = async (serviceType: Partial<ServiceTypeDto>): Promise<ServiceTypeDto> => {
  const { data } = await api.post<ServiceTypeDto>('/servicetypes', serviceType);
  return data;
};

export const updateServiceType = async (id: number, serviceType: Partial<ServiceTypeDto>): Promise<ServiceTypeDto> => {
  const { data } = await api.put<ServiceTypeDto>(`/servicetypes/${id}`, serviceType);
  return data;
};

export const deleteServiceType = async (id: number): Promise<void> => {
  await api.delete(`/servicetypes/${id}`);
};

// Bank Account (Banka Hesabı) Services
export interface BankAccountDto {
  id: number;
  bankName: string;
  accountName: string;
  accountCode?: string;
  iban?: string;
  branchId?: number;
  branchName?: string;
  openingBalance: number;
  currentBalance: number;
  currency: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
}

export const fetchBankAccounts = async (): Promise<BankAccountDto[]> => {
  const { data } = await api.get<BankAccountDto[]>('/bankaccounts');
  return data;
};

export const createBankAccount = async (payload: Partial<BankAccountDto>): Promise<BankAccountDto> => {
  const { data } = await api.post<BankAccountDto>('/bankaccounts', payload);
  return data;
};

export const updateBankAccount = async (id: number, payload: Partial<BankAccountDto>): Promise<BankAccountDto> => {
  const { data } = await api.put<BankAccountDto>(`/bankaccounts/${id}`, payload);
  return data;
};

export const deleteBankAccount = async (id: number): Promise<void> => {
  await api.delete(`/bankaccounts/${id}`);
};

// Kasadan bankaya virman
export const transferToBank = async (
  cashRegisterId: number,
  request: { bankAccountId: number; amount: number; description?: string }
): Promise<{ message: string; transactionId: number; cashBalanceAfter: number; bankBalanceAfter: number }> => {
  const { data } = await api.post(
    `/cashregisters/${cashRegisterId}/transfer-to-bank`,
    request
  );
  return data;
};

